"use client";
import { LoadingIcon } from "components/ui/vectors/loading-icon";
import { useEffect, useRef, useState } from "react";
import { CustomRow } from "./sub-components/custom-row";
import { CustomTableProps } from "./types";
import {
  Box,
  Center,
  Flex,
  IconButton,
  IconButtonProps,
  Tooltip,
  useColorMode,
  useStyleConfig,
} from "@chakra-ui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { TableOrderIcon } from "./order-icon";
import { makeEvent } from "utils/events";
import { perfLogSampled } from "utils/performance-logs";

export function CustomTable({
  columns = [],
  rows: originalRows = [],
  pageSize = 10,
  onClickRow,
  isLoading = false,
  emptyMessage = "Sem dados",
  containerStyle,
  size = "full",
  onChangePage,
  currentPage,
  className,
  footerRow,
  renderTableHeader,
  withOutFooter = false,
  rowStyle,
  LineBellow,
  tableStyle = "custom",
  loadingContainerProps,
  doubleClickInRow,
  tableTitle,
  tableTitleRender,
  lineHoverColor,
  customLoading,
  tableProps,
  boxTableProps,
  constructContextMenu,
  openContextMenuOnMouseEnter = false,
  openContextMenuOnLeftClick = false,
  topRowStyle,
  constructRowId,
  customTableBoxStyle,
  footerStyle = "",
  animateRowsOnAppear = false,
  tableFooterContainerProps,
  buttonsContainer,
  customRowPairColor,
  tableHeaderProps,
}: CustomTableProps) {
  const [indexPage, setIndexPage] = useState(0);
  const [orderedKey, setOrderedKey] = useState<string>("");
  const [orderType, setOrderType] = useState<"asc" | "des">("asc");
  const [orderedRows, setOrderedRows] = useState<any[]>();
  // Linhas que já foram exibidas/animação concluída. Evita repetir o blur ao
  // trocar de página e voltar para linhas que o usuário já viu.
  const seenRowsRef = useRef<Set<any>>(new Set());
  // Linhas que estão montadas e animando na página atual (mantém o blur ativo
  // mesmo com re-renders, até a animação de 2s concluir).
  const animatingRowsRef = useRef<Set<any>>(new Set());
  const cardStyles = useStyleConfig("Card");
  const { colorMode } = useColorMode();
  const border =
    tableStyle === "classic" || tableStyle === "custom"
      ? (cardStyles.border as string)
      : "";

  const rows = orderedRows ?? originalRows;

  const paginedData: any[][] = [];
  if (pageSize != null) {
    let page: any[] = [];
    rows.forEach((item, index) => {
      page.push(item);
      if ((index + 1) % pageSize === 0) {
        paginedData.push(page);
        page = [];
      } else if (index + 1 === rows.length) {
        paginedData.push(page);
      }
    });
  }
  const currPage = onChangePage
    ? rows
    : pageSize != null
      ? paginedData[indexPage]
      : rows;

  const nOfPages = paginedData.length;

  let pageNumberArray: number[] = [];
  for (let k = 0; k < nOfPages; k++) pageNumberArray.push(k);
  if (indexPage - 2 >= 0 && nOfPages > 5) {
    const cut: number[] = pageNumberArray.slice(indexPage - 2, indexPage + 3);
    if (cut.length < 5)
      pageNumberArray = pageNumberArray.slice(nOfPages - 5, nOfPages);
    else pageNumberArray = cut;
  } else pageNumberArray = pageNumberArray.slice(0, 5);

  const handleOrder = (key: string) => {
    let order = orderType;
    let localRows = originalRows;

    if (key !== orderedKey) {
      setOrderedKey(key);
      order = "asc";
      setOrderType(order);
    } else if (order === "des") {
      setOrderedRows(originalRows);
      setOrderedKey("");
      return;
    } else {
      order = order === "asc" ? "des" : "asc";
      setOrderType(order);
    }

    const sortedRows = [...localRows].sort((a, b) => {
      // Order String
      if (typeof a[key] === "string") {
        if (order === "asc") {
          return a[key].localeCompare(b[key]);
        }
        return b[key].localeCompare(a[key]);
      } else if (typeof a[key] === "number") {
        if (order === "asc") {
          return a[key] - b[key];
        }
        return b[key] - a[key];
      }
      return false;
    });

    setOrderedRows(sortedRows);
  };

  useEffect(() => {
    if (currentPage !== undefined) setIndexPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setOrderedRows(undefined);
    setOrderedKey("");
    setOrderType("asc");
  }, [currentPage, isLoading]);

  // [PERF] Diagnóstico: volume de linhas renderizadas pela tabela a cada
  // renderização (amostrado). Se o pageSize for grande ou o modo "selecionadas"
  // montar muitas linhas, o custo por linha (tags, etc) se multiplica.
  useEffect(() => {
    perfLogSampled("customTable:renderRows", {
      rowsCount: currPage?.length ?? 0,
      pageSize: pageSize ?? 0,
    });
  }, [currPage, pageSize]);

  // Quando a página (ou os dados) muda, as linhas que deixaram de ser
  // exibidas passam a ser consideradas "vistas", para que a animação de blur
  // não se repita caso o usuário navegue de volta para elas.
  useEffect(() => {
    if (!animateRowsOnAppear) return;
    const currentPageRows =
      onChangePage || pageSize == null
        ? rows
        : rows.slice(indexPage * pageSize, indexPage * pageSize + pageSize);
    const currentPageSet = new Set(currentPageRows);
    const nextAnimating = new Set<any>();
    animatingRowsRef.current.forEach((row) => {
      if (currentPageSet.has(row)) {
        nextAnimating.add(row);
      } else {
        seenRowsRef.current.add(row);
      }
    });
    animatingRowsRef.current = nextAnimating;
  }, [rows, indexPage, pageSize, onChangePage, animateRowsOnAppear]);

  const maxIndex = nOfPages ? nOfPages - 1 : 0;
  if (pageSize) if (indexPage > maxIndex) setIndexPage(maxIndex);

  const cssTablePrefix =
    tableStyle === "custom"
      ? ""
      : tableStyle === "invisible"
        ? "-invisible"
        : "-classic";

  const smallFooterStyle: IconButtonProps = {
    "aria-label": "pagina",
    width: "24px",
    height: "24px",
    minW: "0px !important",
  };

  // Decide se a linha deve receber a animação de aparecimento. Somente a
  // primeira exibição de cada linha (objeto) anima; reexibições (ex.: voltar
  // para uma página já visitada) não repetem o blur.
  const shouldAnimateRow = (row: any) => {
    if (!animateRowsOnAppear) return false;
    const isSeen = seenRowsRef.current.has(row);
    const isAnimating = animatingRowsRef.current.has(row);
    if (!isSeen && !isAnimating) {
      animatingRowsRef.current.add(row);
    }
    return !isSeen;
  };

  return (
    <Box
      className={`table-container${cssTablePrefix} ${colorMode}`}
      style={{
        border,
        paddingLeft: renderTableHeader ? "24px" : "",
        paddingRight: renderTableHeader ? "24px" : "",
        ...containerStyle,
      }}
      {...boxTableProps}
      onMouseOut={(e) => {
        if (openContextMenuOnMouseEnter === true) {
          const currentTarget = e.currentTarget as HTMLElement;
          const related = e.relatedTarget as Node | null;

          if (!related || !currentTarget.contains(related)) {
            makeEvent("closeContextMenu");
          }
        }
        boxTableProps?.onMouseOut?.(e);
      }}
    >
      {renderTableHeader ? (
        <div className={`table-title-container${cssTablePrefix}`}>
          {renderTableHeader}
        </div>
      ) : null}
      <div
        className={`table-box${
          className ? ` ${className}` : ""
        }${cssTablePrefix}`}
        style={customTableBoxStyle}
        // style={{ paddingBottom: pageSize && nOfPages > 1 ? "0" : undefined }}
      >
        <table {...tableProps}>
          <thead>
            {(tableTitleRender?.() ?? tableTitle) ? (
              <tr className={`table-title${cssTablePrefix}`}>
                <th
                  colSpan={columns.length}
                  className={`table-title-cell${cssTablePrefix}`}
                  style={{
                    borderRight: "0px",
                  }}
                >
                  {tableTitleRender?.() ?? tableTitle}
                </th>
              </tr>
            ) : null}
            <Box
              as="tr"
              className={`table-header${cssTablePrefix}`}
              {...tableHeaderProps}
            >
              {columns.map(
                (
                  {
                    name,
                    headRender,
                    headRenderWithAllRows,
                    isVisible = true,
                    sortClick,
                    key,
                    sortDescribe,
                    headTrProps,
                  },
                  index,
                ) => {
                  const headerClassName =
                    tableStyle === "classic" || tableStyle === "invisible"
                      ? index === columns.length - 1
                        ? `table-header-cell${cssTablePrefix}-last`
                        : `table-header-cell${cssTablePrefix}`
                      : "table-data-cell";

                  return isVisible ? (
                    <th
                      key={`column-${name}-${index}`}
                      className={headerClassName}
                      {...headTrProps}
                    >
                      <Box
                        {...(sortClick
                          ? {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                            }
                          : {})}
                      >
                        <div className={`header-cell-align${cssTablePrefix}`}>
                          {headRenderWithAllRows?.(currPage) ?? headRender?.()}
                          {name}{" "}
                        </div>
                        {sortClick ? (
                          <Box
                            onClick={() => {
                              handleOrder(key);
                            }}
                          >
                            <Tooltip label={sortDescribe}>
                              <IconButton
                                p="0px !important"
                                w="5px !important"
                                h="20px !important"
                                aria-label=""
                                icon={
                                  <TableOrderIcon
                                    elementKey={key}
                                    orderType={orderType}
                                    orderedKey={orderedKey}
                                  />
                                }
                              />
                            </Tooltip>
                          </Box>
                        ) : null}
                      </Box>
                    </th>
                  ) : null;
                },
              )}
            </Box>
          </thead>
          <tbody>
            {isLoading
              ? null
              : currPage?.map((row, rowIndex) => {
                  return (
                    <>
                      <CustomRow
                        key={`row-${rowIndex}-${indexPage}`}
                        columns={columns}
                        onClickRow={onClickRow}
                        row={row}
                        rowIndex={rowIndex}
                        rowStyle={rowStyle}
                        LineBellow={LineBellow}
                        tableStyle={tableStyle}
                        doubleClickInRow={doubleClickInRow}
                        numberOfRows={currPage.length}
                        lineHoverColor={lineHoverColor}
                        constructContextMenu={constructContextMenu}
                        openContextMenuOnMouseEnter={
                          openContextMenuOnMouseEnter
                        }
                        openContextMenuOnLeftClick={openContextMenuOnLeftClick}
                        topRowStyle={topRowStyle}
                        constructRowId={constructRowId}
                        animateRowsOnAppear={shouldAnimateRow(row)}
                        customRowPairColor={customRowPairColor}
                      />
                    </>
                  );
                })}
            {isLoading ? null : footerRow ? (
              paginedData.length ? (
                <CustomRow
                  columns={columns}
                  row={footerRow}
                  rowIndex={-1}
                  isFooter={true}
                  rowStyle={rowStyle}
                  tableStyle={tableStyle}
                  doubleClickInRow={doubleClickInRow}
                  numberOfRows={currPage.length}
                  lineHoverColor={lineHoverColor}
                  constructContextMenu={constructContextMenu}
                  openContextMenuOnMouseEnter={openContextMenuOnMouseEnter}
                  openContextMenuOnLeftClick={openContextMenuOnLeftClick}
                  topRowStyle={topRowStyle}
                  constructRowId={constructRowId}
                  customRowPairColor={customRowPairColor}
                />
              ) : null
            ) : null}
          </tbody>
        </table>
        {isLoading
          ? (customLoading ?? (
              <Flex
                className={`loading-container ${size}`}
                w="100%"
                justifyContent={"center"}
                alignItems={"center"}
                {...loadingContainerProps}
              >
                <LoadingIcon width="40px" height="40px" />
              </Flex>
            ))
          : null}
        {isLoading === false &&
        ((currPage ?? []).length === 0 || currPage === undefined) ? (
          <p className={`empty-message${cssTablePrefix}`}>{emptyMessage}</p>
        ) : null}
        {((pageSize && nOfPages > 1) || onChangePage) &&
        !withOutFooter &&
        !isLoading ? (
          <Box
            className={`table-footer-container${footerStyle === "small" ? "-small" : ""}`}
            {...tableFooterContainerProps}
          >
            <Box
              className={`buttons-container${footerStyle === "small" ? "-small" : ""}`}
              {...buttonsContainer}
            >
              <IconButton
                //aria-label="pagina anterior"
                variant="brand"
                size="sm"
                onClick={() => {
                  if (!onChangePage) setIndexPage(0);
                  if (currentPage != null && onChangePage) onChangePage(0);
                }}
                isDisabled={
                  currentPage != null
                    ? currentPage === 0 || isLoading
                    : indexPage === 0 || isLoading
                }
                icon={<ArrowLeftIcon w="10px" h="10px" />}
                {...(footerStyle === "small"
                  ? smallFooterStyle
                  : {
                      "aria-label": "pagina anterior",
                    })}
              />
              <IconButton
                // aria-label="pagina anterior"
                variant="brand"
                size="sm"
                onClick={() => {
                  if (!onChangePage) setIndexPage((state) => state - 1);
                  if (currentPage != null && onChangePage)
                    onChangePage(currentPage - 1);
                }}
                isDisabled={
                  currentPage != null
                    ? currentPage === 0 || isLoading
                    : indexPage === 0 || isLoading
                }
                icon={<ChevronLeftIcon width="18px" height="18px" />}
                {...(footerStyle === "small"
                  ? smallFooterStyle
                  : {
                      "aria-label": "pagina anterior",
                    })}
              />

              {onChangePage && currentPage != null ? (
                <Center
                  border={border}
                  w="30px"
                  h="30px"
                  // bg={bgBoxes}
                  borderRadius="99px"
                  fontWeight="bold"
                >
                  {currentPage + 1}
                </Center>
              ) : (
                pageNumberArray.map((number, index) => {
                  const active = indexPage === number;
                  return (
                    <p
                      key={index}
                      className={`page-number${footerStyle === "small" ? "-small" : ""}`}
                      style={{
                        background: active
                          ? colorMode === "light"
                            ? "var(--gray-200)"
                            : "var(--chakra-colors-whiteAlpha-100)"
                          : "",
                      }}
                      onClick={() => setIndexPage(number)}
                    >
                      {number + 1}
                    </p>
                  );
                })
              )}
              <IconButton
                // aria-label="proxima pagina"
                variant="brand"
                size="sm"
                onClick={() => {
                  if (!onChangePage) setIndexPage((state) => state + 1);
                  if (currentPage != null && onChangePage)
                    onChangePage(currentPage + 1);
                }}
                icon={<ChevronRightIcon width="18px" height="18px" />}
                isDisabled={
                  (paginedData.length <= indexPage + 1 && !onChangePage) ||
                  isLoading
                }
                {...(footerStyle === "small"
                  ? smallFooterStyle
                  : {
                      "aria-label": "proxima pagina",
                    })}
              />
              {onChangePage ? null : (
                <IconButton
                  // aria-label="proxima pagina"
                  variant="brand"
                  size="sm"
                  onClick={() => {
                    setIndexPage(paginedData.length - 1);
                    // onChangePage?.(indexPage + 1);
                  }}
                  icon={<ArrowRightIcon w="10px" h="10px" />}
                  isDisabled={paginedData.length <= indexPage + 1}
                  {...(footerStyle === "small"
                    ? smallFooterStyle
                    : {
                        "aria-label": "proxima pagina",
                      })}
                />
              )}
            </Box>
          </Box>
        ) : null}
      </div>
    </Box>
  );
}
